import React, { FormEvent, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useAlerts } from '../../../hooks/useAlerts';
import { useAuthUsers, useDomainPath } from '../../../auth/auth.context';
import { get, post, put, httpDelete, widgetLoaderUrl } from '../../../fetch';
import { AlertDialog } from '../../../components/alerts/AlertDialog.component';

interface WidgetConfigurationDialogProps {
  open: boolean;
  onClose: () => void;
  activityId: string;
  activityName: string; // Nom de l'activité (champ 'name' utilisé pour l'intégration)
  pricingGroup: string;
  pricingGroupName: string;
  widgetId?: string; // ID du widget à éditer (optionnel, si non fourni = création)
  onSaved: () => void;
}

/**
 * Dialog pour créer ou éditer une configuration de widget
 */
export const WidgetConfigurationDialog: React.FC<WidgetConfigurationDialogProps> = ({
  open,
  onClose,
  activityId,
  activityName,
  pricingGroup,
  pricingGroupName,
  widgetId,
  onSaved,
}) => {
  const { notify } = useAlerts();
  const { domain } = useAuthUsers();
  const path = useDomainPath(`/activities/${activityId}/widget-configuration`);

  const [configId, setConfigId] = useState<string | undefined>();
  const [code, setCode] = useState<string>('');
  const [lang, setLang] = useState<'fr' | 'en'>('fr');
  const [primaryColor, setPrimaryColor] = useState<string>('#1976d2');
  const [thankYouUrl, setThankYouUrl] = useState<string>('');
  const [reservableDelayHours, setReservableDelayHours] = useState<number>(0);
  const [allowedDomains, setAllowedDomains] = useState<string[]>(['']);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * Charge la configuration existante si elle existe
   */
  useEffect(() => {
    if (open && activityId && pricingGroup) {
      setIsLoading(true);
      if (widgetId) {
        // Charger un widget spécifique pour édition
        get<CRM.WidgetConfigurationDocument>(`${path}/by-id/${widgetId}`)
          .then(config => {
            setConfigId(config._id);
            setCode(config.code);
            setLang(config.lang);
            setPrimaryColor(config.primaryColor);
            setThankYouUrl(config.thankYouUrl || '');
            setReservableDelayHours(config.reservableDelayHours);
            setAllowedDomains(
              config.allowedDomains.length > 0
                ? config.allowedDomains
                : [''],
            );
            setIsLoading(false);
          })
          .catch(err => {
            notify('Erreur lors du chargement de la configuration', 'error');
            setIsLoading(false);
          });
      } else {
        // Réinitialiser pour création
        setConfigId(undefined);
        setCode('');
        setLang('fr');
        setPrimaryColor('#1976d2');
        setThankYouUrl('');
        setReservableDelayHours(0);
        setAllowedDomains(['']);
        setIsLoading(false);
      }
    }
  }, [open, activityId, pricingGroup, widgetId, path, notify]);

  /**
   * Réinitialise le formulaire à la fermeture
   */
  useEffect(() => {
    if (!open) {
      setConfigId(undefined);
      setCode('');
      setLang('fr');
      setPrimaryColor('#1976d2');
      setThankYouUrl('');
      setReservableDelayHours(0);
      setAllowedDomains(['']);
      setShowDeleteDialog(false);
    }
  }, [open]);

  /**
   * Gère l'ajout d'un domaine
   */
  const handleAddDomain = () => {
    setAllowedDomains([...allowedDomains, '']);
  };

  /**
   * Gère la modification d'un domaine
   */
  const handleDomainChange = (index: number, value: string) => {
    const newDomains = [...allowedDomains];
    newDomains[index] = value;
    setAllowedDomains(newDomains);
  };

  /**
   * Gère la suppression d'un domaine
   */
  const handleRemoveDomain = (index: number) => {
    if (allowedDomains.length > 1) {
      const newDomains = allowedDomains.filter((_, i) => i !== index);
      setAllowedDomains(newDomains);
    }
  };

  /**
   * Valide et sauvegarde la configuration
   */
  const handleSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Validation
    if (!code.trim()) {
      notify('Le code du widget est requis', 'error');
      return;
    }

    // Valider le format du code (lettres, chiffres, tirets, underscores uniquement)
    if (!/^[a-zA-Z0-9_-]+$/.test(code.trim())) {
      notify('Le code ne peut contenir que des lettres, chiffres, tirets et underscores', 'error');
      return;
    }

    const filteredDomains = allowedDomains.filter(d => d.trim() !== '');
    if (filteredDomains.length === 0) {
      notify('Au moins un domaine autorisé est requis', 'error');
      return;
    }

    if (reservableDelayHours < 0) {
      notify('Le délai de réservation doit être positif', 'error');
      return;
    }

    try {
      const data: CRM.CreateWidgetConfiguration = {
        activity: activityId,
        pricingGroup: pricingGroup,
        code: code.trim(),
        lang: lang,
        primaryColor: primaryColor,
        thankYouUrl: thankYouUrl.trim() || undefined,
        reservableDelayHours: reservableDelayHours,
        allowedDomains: filteredDomains,
      };

      if (configId) {
        // Mise à jour
        await put<CRM.CreateWidgetConfiguration>(`${path}/${configId}`, data);
        notify('Configuration mise à jour avec succès', 'success');
      } else {
        // Création
        await post<CRM.CreateWidgetConfiguration>(path, data);
        notify('Configuration créée avec succès', 'success');
      }

      onSaved();
      onClose();
    } catch (err) {
      notify('Erreur lors de la sauvegarde', 'error');
    }
  };

  /**
   * Supprime la configuration
   */
  const handleDelete = async () => {
    if (!configId) return;

    try {
      await httpDelete(`${path}/${configId}`);
      notify('Configuration supprimée avec succès', 'success');
      onSaved();
      onClose();
    } catch (err) {
      notify('Erreur lors de la suppression', 'error');
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="widget-config-dialog-title"
      >
        <form onSubmit={handleSave}>
          <DialogTitle id="widget-config-dialog-title">
            <Box display="flex" alignItems="center">
              <Box flexGrow={1}>
                <Typography variant="h6">
                  {configId
                    ? `Éditer le widget - ${pricingGroupName}`
                    : `Créer le widget - ${pricingGroupName}`}
                </Typography>
              </Box>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {isLoading ? (
              <Typography>Chargement...</Typography>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Code du widget *"
                    value={code}
                    onChange={e => setCode(e.target.value)}
                    fullWidth
                    variant="outlined"
                    required
                    placeholder="site-a"
                    helperText="Identifiant unique (lettres, chiffres, tirets, underscores uniquement)"
                    inputProps={{
                      pattern: '[a-zA-Z0-9_-]+',
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" required>
                    <InputLabel>Langue</InputLabel>
                    <Select
                      value={lang}
                      onChange={e =>
                        setLang(e.target.value as 'fr' | 'en')
                      }
                      label="Langue"
                    >
                      <MenuItem value="fr">Français</MenuItem>
                      <MenuItem value="en">English</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Couleur principale (hexadécimal)"
                    value={primaryColor}
                    onChange={e => {
                      const value = e.target.value;
                      // Valider le format hexadécimal
                      if (value === '' || /^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                        setPrimaryColor(value);
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    required
                    placeholder="#1976d2"
                    helperText="Format hexadécimal (ex: #1976d2)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <input
                            type="color"
                            value={primaryColor}
                            onChange={e => setPrimaryColor(e.target.value)}
                            style={{
                              width: '40px',
                              height: '40px',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              padding: 0,
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="URL de remerciement (optionnel)"
                    value={thankYouUrl}
                    onChange={e => setThankYouUrl(e.target.value)}
                    fullWidth
                    variant="outlined"
                    placeholder="https://example.com/merci"
                    helperText="URL vers laquelle rediriger après le paiement"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Réservable à H + X"
                    type="number"
                    value={reservableDelayHours}
                    onChange={e =>
                      setReservableDelayHours(Number(e.target.value))
                    }
                    fullWidth
                    variant="outlined"
                    required
                    inputProps={{ min: 0, step: 0.5 }}
                    helperText="Délai en heures avant le premier créneau disponible"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Domaines autorisés *
                  </Typography>
                  <FormHelperText>
                    Liste des domaines autorisés à intégrer ce widget (ex:
                    example.com, www.example.com)
                  </FormHelperText>
                  {allowedDomains.map((domain, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      mb={1}
                    >
                      <TextField
                        value={domain}
                        onChange={e =>
                          handleDomainChange(index, e.target.value)
                        }
                        variant="outlined"
                        placeholder="example.com"
                        fullWidth
                        style={{ marginRight: 8 }}
                      />
                      {allowedDomains.length > 1 && (
                        <Button
                          onClick={() => handleRemoveDomain(index)}
                          color="secondary"
                          variant="outlined"
                        >
                          Supprimer
                        </Button>
                      )}
                    </Box>
                  ))}
                  <Button
                    onClick={handleAddDomain}
                    color="primary"
                    variant="outlined"
                    size="small"
                  >
                    + Ajouter un domaine
                  </Button>
                </Grid>
                {configId && (
                  <Grid item xs={12}>
                    <Box
                      p={2}
                      bgcolor="grey.100"
                      borderRadius={2}
                      border={1}
                      borderColor="divider"
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        Code d'intégration
                      </Typography>
                      <Box
                        component="pre"
                        style={{
                          margin: 0,
                          padding: '8px',
                          backgroundColor: '#fff',
                          borderRadius: '4px',
                          fontSize: '12px',
                          overflow: 'auto',
                        }}
                      >
                        {`<div id="naturesa-booking-widget"
     data-activity-id="${activityName}"
     data-pricing-group="${pricingGroup}"
     data-widget-code="${code}"
     data-domain-name="${domain?.name || ''}">
</div>
<script src="${widgetLoaderUrl}/loader.js"></script>`}
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          const integrationCode = `<div id="naturesa-booking-widget"
     data-activity-id="${activityName}"
     data-pricing-group="${pricingGroup}"
     data-widget-code="${code}"
     data-domain-name="${domain?.name || ''}">
</div>
<script src="${widgetLoaderUrl}/loader.js"></script>`;
                          navigator.clipboard.writeText(integrationCode);
                          notify('Code copié dans le presse-papiers', 'success');
                        }}
                        style={{ marginTop: '8px', marginRight: '8px' }}
                      >
                        Copier le code
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          const buttonText = lang === 'en' ? 'Book' : 'Réserver';
                          const integrationCode = `<div id="naturesa-booking-widget"
     data-activity-id="${activityId}"
     data-pricing-group="${pricingGroup}"
     data-widget-code="${code}"
     data-domain-name="${domain?.name || ''}"
     data-display-mode="button"
     data-button-text="${buttonText}">
</div>
<script src="${widgetLoaderUrl}/loader.js"></script>`;
                          navigator.clipboard.writeText(integrationCode);
                          notify('Code copié dans le presse-papiers', 'success');
                        }}
                        style={{ marginTop: '8px' }}
                      >
                        Copier le code (version bouton)
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            {configId && (
              <Button
                onClick={() => setShowDeleteDialog(true)}
                color="secondary"
                variant="outlined"
              >
                Supprimer
              </Button>
            )}
            <Button onClick={onClose} color="secondary">
              Annuler
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={isLoading}
            >
              {configId ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <AlertDialog
        open={showDeleteDialog}
        title="Confirmer la suppression"
        content="Êtes-vous sûr de vouloir supprimer cette configuration de widget ? Cette action est irréversible."
        accept={handleDelete}
        cancel={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

